import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { CardDensitySelector, KanbanBoard, KanbanSortSelector, rankBetween, resolveNeighborRanks } from '@/shared/components/kanban';
import {
  Button,
  CardActionsMenu,
  ContextSelector,
  OperatorAvatarFilter,
  PageToolbar,
  ToolbarSearch,
  type OperatorAvatarFilterOperator,
} from '@/shared/components/ui';
import { useConfirm } from '@/shared/components/ui/ConfirmProvider';
import { LeadsAdvancedFiltersComponent } from '../components/LeadsAdvancedFilters';
import { FunnelNoUsersBanner } from '../components/FunnelNoUsersBanner';
import { LeadCard, LeadCardAction } from '../components/LeadCard';
import { PopulatedLead, LeadsAdvancedFilters } from '../types';
import type { LeadKanbanCard, AlertLevel } from '@troia-v3/types';
import { LEAD_KANBAN_SORT_MODES } from '@troia-v3/types';
import { useLeadsFilters, useCrmPreferences, useLeadImport, useTransferLead, useLeadsKanban, useLeadOperatorsDropdown } from '../hooks';
import { usePermissions } from '@/hooks/usePermissions';
import { useClearStaleOperator } from '@/shared/hooks/useClearStaleOperator';
import { toOperatorFilterOperator } from '@/shared/utils/contextOperators';
import { useFunnelsDropdown, FunnelsSettingsDrawer, useStepsByFunnelId } from '@/modules/funnels';
import { LeadCaptureModal, useLeadCaptureOptions } from '@/modules/crm/capture';
import { LeadFormDrawer } from '../components/LeadFormDrawer';
import { DeleteLeadDialog } from '../components/DeleteLeadDialog';
import { LeadDetailsDialog } from '../components/LeadDetailsDialog';
import { TransferLeadDialog } from '../components/TransferLeadDialog';
import { WinLeadDialog, type WinLeadData } from '../components/WinLeadDialog';
import { LoseLeadDialog, type LoseLeadData } from '../components/LoseLeadDialog';
import { AlertSnoozeDialog } from '../components/AlertSnoozeDialog';
import { ImportLeadsDialog } from '../components/ImportLeadsDialog';
import { leadsService } from '../services/leadsService';
import { mapScopeToDropdownFlag, type LeadsScope } from '../utils/leadsAdapter';
import { Icon } from '@/shared/icons'
/**
 * Filtros padrão aplicados pelo backend
 */
const DEFAULT_FILTERS: LeadsAdvancedFilters = {
  businessStatus: ['pending']
};

/**
 * CRM Dashboard Page
 *
 * Dashboard principal do módulo CRM com Kanban de leads por funil.
 * Design baseado no AttendanceManagementPageImproved com:
 * - Sidebar lateral de funis (expandível/recolhível)
 * - Header com métricas destacadas
 * - FilterBar horizontal sempre visível
 * - Kanban com colunas baseadas nas etapas do funil
 * - Botão de configurações para gerenciar funis
 *
 * @example
 * Route: /crm/dashboard
 */
export const CrmDashboardPage: React.FC = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { hasModulePermission } = usePermissions();

  // URL params: /crm/:funnelId?leadId=xxx&stepId=xxx
  const { funnelId: urlFunnelId } = useParams<{ funnelId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const urlLeadId = searchParams.get('leadId');

  // Refs para controlar processamento de params da URL
  // urlFunnelIdProcessed: true após aplicar o funnelId da URL (apenas na montagem inicial)
  // lastProcessedLeadId: armazena qual leadId já foi processado para evitar reprocessamento
  const urlFunnelIdProcessed = useRef(false);
  const lastProcessedLeadId = useRef<string | null>(null);

  // Scope vem de `funnels-users.scope` per-funil (D-context-gate, NUNCA #47).
  // No kanban (per-funil), usa `userScope` do funil selecionado. Calculado
  // mais abaixo após `selectedFunnelId` resolver — placeholder até lá:
  // valores derivados de scope ficam no useMemo abaixo dependendo de
  // `selectedFunnel?.userScope`.

  // CRM Preferences (persisted in localStorage)
  const {
    selectedFunnelId,
    setSelectedFunnelId,
    advancedFilters: persistedFilters,
    setAdvancedFilters: setPersistedFilters,
    sortMode,
    setSortMode,
    cardDensity,
    setCardDensity
  } = useCrmPreferences();

  // State management (not persisted)
  const [searchTerm, setSearchTerm] = useState('');
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [settingsInitialView, setSettingsInitialView] = useState<'list' | 'create-funnel'>('list');
  // Deep-link do aviso "funil sem usuários": abre o drawer já neste funil, aba Usuários
  const [settingsInitialFunnelId, setSettingsInitialFunnelId] = useState<string | null>(null);
  const [isCreateLeadOpen, setIsCreateLeadOpen] = useState(false);

  // Captura por QR (Parte 2): botão só com crm:create E pelo menos um funil
  // com captura em que o user atende (spec §4.8 / §6 primeira linha).
  const canCapture = hasModulePermission('crm', 'create');
  const captureOptions = useLeadCaptureOptions(canCapture);
  const showCaptureButton = canCapture && (captureOptions.data?.funnels.length ?? 0) > 0;
  const [isCaptureOpen, setIsCaptureOpen] = useState(false);

  // Zerar cupons de sorteio: mesma permissão de excluir leads (apaga dado de
  // todos os leads da empresa e não tem volta).
  const canResetCoupons = hasModulePermission('crm', 'delete');
  const confirm = useConfirm();
  const handleResetCoupons = async () => {
    const ok = await confirm({
      title: 'Zerar cupons',
      message: 'Os cupons de sorteio de TODOS os leads serão apagados e a numeração recomeça do 1.',
      warningMessage: 'Não dá para desfazer. Se precisar da lista de quem tinha cada cupom, exporte os leads antes.',
      confirmButtonText: 'Zerar cupons',
    });
    if (!ok) return;
    try {
      const { clearedLeads } = await leadsService.resetCoupons();
      toast.success(
        clearedLeads === 1 ? 'Cupons zerados em 1 lead.' : `Cupons zerados em ${clearedLeads} leads.`,
      );
      refetchAll();
    } catch (error) {
      console.error('Erro ao zerar cupons:', error);
      toast.error('Não foi possível zerar os cupons. Tente novamente.');
    }
  };
  const crmMenuActions = canResetCoupons
    ? [{ label: 'Zerar cupons', icon: 'trash' as const, variant: 'danger' as const, onClick: () => { void handleResetCoupons(); }, testId: 'crm-reset-coupons' }]
    : [];

  // Deep-link `?captureSession={uuid}`: abre a modal retomando uma sessão
  // conhecida (browser tests da Task 17 e retomada pelo dashboard/notificação).
  const captureSessionParam = searchParams.get('captureSession');
  useEffect(() => {
    if (captureSessionParam) setIsCaptureOpen(true);
  }, [captureSessionParam]);

  // Fecha a modal e, se ela tiver sido aberta por deep-link, remove o param
  // da URL sem recarregar a página (evita reabrir a mesma sessão ao navegar).
  const handleCaptureClose = useCallback(() => {
    setIsCaptureOpen(false);
    if (captureSessionParam) {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.delete('captureSession');
        return next;
      }, { replace: true });
    }
  }, [captureSessionParam, setSearchParams]);

  // Dialog states
  const [selectedLead, setSelectedLead] = useState<PopulatedLead | null>(null);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isTransferOpen, setIsTransferOpen] = useState(false);
  const [isWinOpen, setIsWinOpen] = useState(false);
  const [isLoseOpen, setIsLoseOpen] = useState(false);
  const [isImportDialogOpen, setIsImportDialogOpen] = useState(false);
  const [importStepId, setImportStepId] = useState<string | null>(null);

  // Alertas de inatividade — snooze dialog + filtro por cor (kanban)
  const [isSnoozeOpen, setIsSnoozeOpen] = useState(false);
  const [snoozeLead, setSnoozeLead] = useState<PopulatedLead | LeadKanbanCard | null>(null);
  const [alertLevelFilter, setAlertLevelFilter] = useState<AlertLevel | undefined>(undefined);

  // Bulk selection state

  // Lead Import Progress
  const {
    isImporting,
    currentItem,
    totalItems,
    message: importMessage,
    percentage: importPercentage
  } = useLeadImport({
    stepId: importStepId,
    onImportCompleted: () => {
      refetchAll(); // Auto-refresh leads (lista + kanban) quando importação concluir
    }
  });

  // Filtros avançados (sincronizados com localStorage via useCrmPreferences)
  const {
    advancedFilters,
    appliedFilters,
    selectedOperatorIds,
    handleAdvancedFiltersChange,
    handleOperatorSelectionChange,
    handleApplyFilters,
    toLeadQuery
  } = useLeadsFilters();

  // Inicializar filtros ao carregar
  useEffect(() => {
    const hasLocalStorage = localStorage.getItem('crm_advanced_filters') !== null;

    if (hasLocalStorage) {
      // Se tem no localStorage, usar o que está lá (mesmo que seja {})
      handleAdvancedFiltersChange(persistedFilters);
      handleApplyFilters(persistedFilters);
    } else {
      // Primeira carga SEM localStorage: usar defaults E salvar no localStorage
      handleAdvancedFiltersChange(DEFAULT_FILTERS);
      handleApplyFilters(DEFAULT_FILTERS);
      // Salvar defaults no localStorage imediatamente via hook
      // O hook useCrmPreferences tem um useEffect que automaticamente salva no localStorage
      setPersistedFilters(DEFAULT_FILTERS);
    }
  }, []); // Executar apenas na montagem

  // Função wrapper para aplicar filtros automaticamente
  const handleFiltersChange = (filters: any) => {
    handleAdvancedFiltersChange(filters);
    // ✅ CRÍTICO: Passar filtros diretamente para evitar race condition com state assíncrono
    // Se não passarmos, handleApplyFilters() usará o valor ANTIGO de advancedFilters
    handleApplyFilters(filters);
    // Salvar no localStorage via hook (que já tem skip first save)
    setPersistedFilters(filters);
  };

  // Filtros avançados para o kanban — mesmo objeto que toLeadQuery mescla hoje
  // (advancedFilters + assigneeId de selectedOperatorIds), mas sem funnelId
  // (o board recebe funnelId como parâmetro top-level, não dentro de filters).
  // Memoizado pelas dependências reais que alimentam toLeadQuery — sem isso,
  // um objeto novo a cada render muda a identidade de `filters`, recriando
  // `loadMore` e desmontando/remontando o sentinel de scroll da coluna.
  const kanbanFilters = useMemo(() => {
    const base = toLeadQuery().filters;
    if (!alertLevelFilter) return base;
    return { ...base, alertLevel: alertLevelFilter };
  }, [toLeadQuery, appliedFilters, selectedOperatorIds, alertLevelFilter]);

  const {
    boardData,
    isLoading: kanbanLoading,
    error: kanbanError,
    refetch: refetchKanban,
    loadMore,
    applyCardMove,
    applyCardReorder,
    loadingColumns,
    errorColumns,
  } = useLeadsKanban({
    funnelId: selectedFunnelId,
    search: searchTerm.trim() || undefined,
    filters: kanbanFilters,
    sortMode,
  });

  const isLoading = kanbanLoading;
  const error = kanbanError;

  // Invalida a chave `leads` além de recarregar o board: drawers e modais desta
  // tela (detalhe do lead, atividades) leem por ela, e uma listagem servida do
  // cache depois de criar/editar/excluir mostraria o estado anterior.
  const refetchAll = () => {
    queryClient.invalidateQueries({ queryKey: ['leads'] });
    refetchKanban();
  };

  const transferLeadMutation = useTransferLead();

  // Buscar funis da API
  const { data: funnelsData } = useFunnelsDropdown();

  // Scope efetivo no funil selecionado (kanban é per-funil, então `userScope`
  // vem direto do funil ativo). Hidratado pelo backend a partir de funnels-users.
  const selectedFunnelData = useMemo(
    () => funnelsData?.find((f) => f.id === selectedFunnelId),
    [funnelsData, selectedFunnelId],
  );

  // Ao trocar de funil, as perguntas do modelo anterior não fazem sentido —
  // remove `formAnswers` persistidas (localStorage) antes da primeira query.
  //
  // A transição `null → id` NÃO é troca de funil: é o funil RESOLVENDO no
  // carregamento da página (URL `?funnelId=`, localStorage ou fallback do
  // primeiro funil — ver o efeito de auto-seleção mais abaixo). Tratá-la como
  // troca apagava o filtro de formulário que o usuário tinha deixado salvo,
  // toda vez que ele recarregava o CRM. Só limpa quando o funil anterior era
  // um id REAL e diferente do novo.
  const previousFunnelRef = useRef<string | null>(selectedFunnelId);
  useEffect(() => {
    const previousFunnelId = previousFunnelRef.current;
    if (previousFunnelId === selectedFunnelId) return;
    previousFunnelRef.current = selectedFunnelId;
    if (!previousFunnelId) return; // primeira resolução do funil, não é troca
    // Formulário escolhido no filtro não depende do funil: as perguntas continuam valendo.
    if (advancedFilters.formAnswersFormId) return;
    if (advancedFilters.formAnswers && advancedFilters.formAnswers.length > 0) {
      const { formAnswers: _omit, ...rest } = advancedFilters;
      handleFiltersChange(rest);
    }
  }, [selectedFunnelId, advancedFilters, handleFiltersChange]);

  const userLeadsScope: LeadsScope = (selectedFunnelData?.userScope ?? 'own') as LeadsScope;
  const showOperatorFilter = userLeadsScope !== 'own';
  const operatorsDropdownScope = mapScopeToDropdownFlag(userLeadsScope);

  // Operadores do funil escolhido: quem participa dele + quem tem lead
  // atribuído nele (mesmo desativado/excluído). `scope=team` restringe aos
  // colegas de equipe do user logado.
  const { data: operatorsData } = useLeadOperatorsDropdown({
    funnelIds: selectedFunnelId ? [selectedFunnelId] : [],
    ...(operatorsDropdownScope ? { scope: operatorsDropdownScope } : {}),
    enabled: showOperatorFilter,
  });

  // Buscar etapas do funil selecionado
  const { data: funnelStepsData } = useStepsByFunnelId(selectedFunnelId || undefined);

  // Funis do dropdown (consumidos pelo ContextSelector do header)
  const funnels = useMemo(() => {
    if (!funnelsData) return [];
    // ✅ dropdown já retorna array de funis ativos, não precisa filtrar
    return funnelsData.map(f => ({
      id: f.id,
      name: f.name,
      color: f.color,
    }));
  }, [funnelsData]);

  // Auto-selecionar funil ao carregar (prioriza URL apenas na PRIMEIRA vez, depois localStorage, depois primeiro)
  useEffect(() => {
    if (funnels.length > 0) {
      // Se há funnelId na URL e ainda não foi processado, usar ele (apenas na montagem inicial)
      if (urlFunnelId && !urlFunnelIdProcessed.current) {
        const funnelExists = funnels.some(f => f.id === urlFunnelId);
        if (funnelExists) {
          setSelectedFunnelId(urlFunnelId);
        }
        // Marcar como processado para que cliques na sidebar funcionem normalmente
        urlFunnelIdProcessed.current = true;
      } else if (selectedFunnelId === null) {
        // Fallback: primeiro funil (quando não há URL funnelId ou já foi processado)
        setSelectedFunnelId(funnels[0].id);
      }
    }
  }, [funnels, selectedFunnelId, urlFunnelId]);

  // Abrir modal de detalhes do lead quando vier da URL — busca por id direto
  // (não depende mais de allLeads, que fica vazio em modo kanban).
  useEffect(() => {
    if (!urlLeadId || lastProcessedLeadId.current === urlLeadId) return;
    lastProcessedLeadId.current = urlLeadId;
    void (async () => {
      try {
        const full = await leadsService.getLeadById(urlLeadId);
        setSelectedLead(full as PopulatedLead);
        setIsDetailsOpen(true);
      } catch {
        // lead inexistente/sem acesso — ignora o deep-link
      } finally {
        setSearchParams({}, { replace: true });
      }
    })();
  }, [urlLeadId, setSearchParams]);

  const operators: OperatorAvatarFilterOperator[] = useMemo(
    () => (operatorsData ?? []).map(toOperatorFilterOperator),
    [operatorsData],
  );

  // Trocou de funil e o operador escolhido não está no novo → limpa o filtro.
  useClearStaleOperator(selectedOperatorIds[0] ?? null, operatorsData, () => handleOperatorSelectionChange([]));

  // Mapear etapas do funil para o formato do Kanban
  const funnelSteps = useMemo(() => {
    if (!funnelStepsData || funnelStepsData.length === 0) return undefined;

    return funnelStepsData.map(step => ({
      id: step.id,
      name: step.name,
      color: step.color || '#8b5cf6', // fallback para roxo
      order: step.order
    }));
  }, [funnelStepsData]);


  // Colunas do Kanban a partir do board (dados por coluna vêm do backend —
  // counts/totalValue/busca já resolvidos; kanban não lê mais filteredLeads).
  const kanbanColumns = useMemo(() => {
    if (!boardData || !funnelSteps) return [];
    const byId = new Map(boardData.columns.map((c) => [c.columnId, c]));
    return [...funnelSteps]
      .sort((a, b) => a.order - b.order)
      .map((step) => {
        const col = byId.get(step.id);
        const items = col?.items ?? [];
        // Mini-barra/legenda (.mk-bar/.mk-legend) por status de alerta dos cards carregados
        const countBy = (lvl: string) => items.filter((i) => i.alertLevel === lvl).length;
        const breakdown = [
          { label: 'Em dia', color: '#10B981', count: countBy('ok') },
          { label: 'Alerta', color: '#F59E0B', count: countBy('warning') },
          { label: 'Crítico', color: '#EF4444', count: countBy('critical') },
          { label: 'Silenciados', color: '#94A3B8', count: countBy('snoozed') },
        ];
        return {
          id: step.id,
          title: step.name,
          color: step.color,
          items,
          count: col?.count ?? 0,
          totalValue: col?.totalValue ?? 0,
          breakdown,
          hasMore: Boolean(col?.nextCursor),
          loadingMore: Boolean(loadingColumns[step.id]),
          loadMoreError: Boolean(errorColumns[step.id]),
        };
      });
  }, [boardData, funnelSteps, loadingColumns, errorColumns]);

  // (métricas agregadas do header antigo saíram — o mockup mostra soma POR COLUNA no board;
  //  o nome do funil ativo agora é exibido pelo próprio ContextSelector)

  // Handler para ações de lead — resolve o lead completo via getLeadById antes
  // de abrir dialogs. Necessário porque o card de origem pode ser o
  // LeadKanbanCard enxuto (kanban), que não tem todos os campos que os
  // dialogs populados (transfer, win, lose, details, edit) precisam.
  const handleLeadAction = async (action: LeadCardAction, lead: PopulatedLead | LeadKanbanCard) => {
    if (action === 'qualify') {
      handleQualifyLead(lead);
      return;
    }
    if (action === 'disqualify') {
      handleDisqualifyLead(lead);
      return;
    }
    if (action === 'snooze') {
      setSnoozeLead(lead);
      setIsSnoozeOpen(true);
      return;
    }
    if (action === 'unsnooze') {
      handleUnsnoozeLead(lead);
      return;
    }

    try {
      const full = await leadsService.getLeadById(lead.id);
      setSelectedLead(full as PopulatedLead);

      switch (action) {
        case 'view':
          setIsDetailsOpen(true);
          break;
        case 'edit':
          setIsEditOpen(true);
          break;
        case 'transfer':
          setIsTransferOpen(true);
          break;
        case 'delete':
          setIsDeleteOpen(true);
          break;
        case 'win':
          setIsWinOpen(true);
          break;
        case 'lose':
          setIsLoseOpen(true);
          break;
        default:
          console.warn(`Ação não implementada: ${action}`);
      }
    } catch (error) {
      console.error('Erro ao carregar lead:', error);
      toast.error('Erro ao carregar os detalhes do lead.');
    }
  };

  // Handler para qualificar lead
  const handleQualifyLead = async (lead: PopulatedLead | LeadKanbanCard) => {
    try {
      await leadsService.updateLead(lead.id, { qualifyStatus: 'qualified' });
      await queryClient.invalidateQueries({ queryKey: ['leads'] });
      await queryClient.invalidateQueries({ queryKey: ['leads-kanban'] });
      toast.success('Lead qualificado com sucesso!');
    } catch (error) {
      console.error('Erro ao qualificar lead:', error);
      toast.error('Erro ao qualificar lead. Tente novamente.');
    }
  };

  // Handler para desqualificar lead
  const handleDisqualifyLead = async (lead: PopulatedLead | LeadKanbanCard) => {
    try {
      await leadsService.updateLead(lead.id, { qualifyStatus: 'disqualified' });
      await queryClient.invalidateQueries({ queryKey: ['leads'] });
      await queryClient.invalidateQueries({ queryKey: ['leads-kanban'] });
      toast.success('Lead desqualificado com sucesso!');
    } catch (error) {
      console.error('Erro ao desqualificar lead:', error);
      toast.error('Erro ao desqualificar lead. Tente novamente.');
    }
  };

  // Handler para reativar alertas (unsnooze direto, sem dialog)
  const handleUnsnoozeLead = async (lead: PopulatedLead | LeadKanbanCard) => {
    try {
      await leadsService.snoozeAlert(lead.id, null);
      await queryClient.invalidateQueries({ queryKey: ['leads'] });
      await queryClient.invalidateQueries({ queryKey: ['leads-kanban'] });
      toast.success('Alertas reativados.');
    } catch (error) {
      console.error('Erro ao reativar alertas:', error);
      toast.error('Erro ao reativar alertas. Tente novamente.');
    }
  };

  // Handler de confirmação do AlertSnoozeDialog — silencia o alerta até a data escolhida
  const handleSnoozeConfirm = async (until: string) => {
    if (!snoozeLead) return;
    try {
      await leadsService.snoozeAlert(snoozeLead.id, until);
      await queryClient.invalidateQueries({ queryKey: ['leads'] });
      await queryClient.invalidateQueries({ queryKey: ['leads-kanban'] });
      toast.success('Alerta silenciado.');
      setIsSnoozeOpen(false);
      setSnoozeLead(null);
    } catch (error) {
      console.error('Erro ao silenciar alerta:', error);
      toast.error('Erro ao silenciar alerta. Tente novamente.');
    }
  };

  // Handler para abrir configurações de funis
  const handleSettingsClick = () => {
    setSettingsInitialView('list');
    setSettingsInitialFunnelId(null);
    setIsSettingsOpen(true);
  };

  // Handler para adicionar novo funil
  const handleAddFunnel = () => {
    setSettingsInitialView('create-funnel');
    setSettingsInitialFunnelId(null);
    setIsSettingsOpen(true);
  };

  // Handler do aviso "funil sem usuários": abre o drawer já no funil
  // selecionado, direto na aba Usuários.
  const handleAssignUsersClick = () => {
    if (!selectedFunnelId) return;
    setSettingsInitialView('list');
    setSettingsInitialFunnelId(selectedFunnelId);
    setIsSettingsOpen(true);
  };

  // Fechar o drawer de configurações: reavalia `hasUsers` (aviso) sempre.
  // Leads/kanban só quando o funil estava órfão — associar usuários ativa o
  // contexto e os leads passam a aparecer; funil já ativo não refetcha o
  // board (preserva as janelas paginadas/scroll das colunas do kanban).
  const handleSettingsClose = () => {
    setIsSettingsOpen(false);
    setSettingsInitialFunnelId(null);
    queryClient.invalidateQueries({ queryKey: ['funnels'] });
    if (selectedFunnelData?.hasUsers === false) {
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      queryClient.invalidateQueries({ queryKey: ['leads-kanban'] });
    }
  };

  // Handler para refresh
  const handleRefresh = () => {
    refetchAll();
  };

  // Handler para click no card — busca o lead completo (o card do kanban é
  // o LeadKanbanCard enxuto, sem os dados que a modal de detalhes precisa).
  const handleCardClick = async (lead: PopulatedLead | LeadKanbanCard) => {
    try {
      const full = await leadsService.getLeadById(lead.id);
      setSelectedLead(full as PopulatedLead);
      setIsDetailsOpen(true);
    } catch (error) {
      console.error('Erro ao carregar lead:', error);
      toast.error('Erro ao carregar os detalhes do lead.');
    }
  };

  // Handler de sucesso do LeadDetailsDialog — refetch lista + kanban, e
  // recarrega o lead selecionado por id para refletir a mudança enquanto o
  // dialog segue aberto (não depende mais de allLeads, vazio em modo kanban).
  // NÃO usar em fluxos que excluem o lead (ver handleDeleteSuccess) — o
  // getLeadById aqui pressupõe que o lead ainda existe.
  const handleDialogSuccess = async () => {
    refetchAll();
    if (selectedLead) {
      try {
        const full = await leadsService.getLeadById(selectedLead.id);
        setSelectedLead(full as PopulatedLead);
      } catch (error) {
        console.error('Erro ao atualizar lead selecionado:', error);
      }
    }
  };

  // Handler de sucesso do DeleteLeadDialog — só refetch (lista + kanban),
  // SEM re-fetch por id. O lead já foi excluído nesse ponto (onSuccess
  // dispara antes do onClose zerar selectedLead) — chamar getLeadById aqui
  // bateria 404 no lead recém-deletado, e o interceptor global do axios
  // (src/lib/api.ts) toastaria esse erro pro usuário em todo delete bem-sucedido.
  const handleDeleteSuccess = () => {
    refetchAll();
    setSelectedLead(null);
  };

  // Handler para transferir lead
  const handleTransferLead = async (
    transferType: 'assignee' | 'funnel',
    data: {
      newAssigneeId?: string;
      newFunnelId?: string;
      newStepId?: string;
    }
  ) => {
    if (!selectedLead) return;

    try {
      if (transferType === 'assignee') {
        // Transferir responsável via endpoint padronizado POST /leads/:id/transfer
        await transferLeadMutation.mutateAsync({
          leadId: selectedLead.id,
          body: { toUserId: data.newAssigneeId ?? null },
        });
      } else {
        // Transferir funil/etapa - atualizar AMBOS funnelId e stepId
        await leadsService.updateLead(selectedLead.id, {
          funnelId: data.newFunnelId!,
          stepId: data.newStepId!,
        });
      }

      // ✅ Invalidar query de leads (lista + kanban) para forçar refetch
      await queryClient.invalidateQueries({ queryKey: ['leads'] });
      await queryClient.invalidateQueries({ queryKey: ['leads-kanban'] });

      // Fechar modal e exibir toast de sucesso
      setIsTransferOpen(false);
      setSelectedLead(null);
      toast.success('Lead transferido com sucesso!');
    } catch (error) {
      console.error('Erro ao transferir lead:', error);
      toast.error('Erro ao transferir lead. Tente novamente.');
      throw error; // Re-throw para o dialog tratar
    }
  };

  // Handler para mover lead entre colunas (etapas do funil) — cirurgia de
  // cache local via applyCardMove (sem refetch do board, preserva o scroll
  // das colunas já carregadas).
  const handleCardMove = async (lead: LeadKanbanCard, fromColumnId: string, toColumnId: string) => {
    try {
      await leadsService.moveLeadToStep(lead.id, toColumnId);
      applyCardMove(lead, fromColumnId, toColumnId);
    } catch (error) {
      console.error('Erro ao mover lead:', error);
      toast.error('Erro ao mover lead. Tente novamente.');
    }
  };

  // Dica exibida no máx. 1× por sessão ao arrastar intra-coluna fora do modo manual
  const reorderHintShown = useRef(false);
  const handleIntraColumnDropBlocked = () => {
    if (reorderHintShown.current) return;
    reorderHintShown.current = true;
    toast('Para reordenar os cards, mude a ordenação para "Personalizado".', { icon: 'ℹ️' });
  };

  const handleCardDrop = async (
    lead: LeadKanbanCard,
    fromColumnId: string,
    toColumnId: string,
    position: { index: number },
  ) => {
    const targetColumn = boardData?.columns.find((c) => c.columnId === toColumnId);
    if (!targetColumn) return;

    // Lista da coluna destino SEM o card movido + índice de inserção ajustado
    const sameColumn = fromColumnId === toColumnId;
    const items = sameColumn ? targetColumn.items.filter((i) => i.id !== lead.id) : targetColumn.items;
    let insertIndex = position.index;
    if (sameColumn) {
      const originalIndex = targetColumn.items.findIndex((i) => i.id === lead.id);
      if (originalIndex !== -1 && originalIndex < position.index) insertIndex = position.index - 1;
    }

    try {
      const { aboveRank, belowRank } = resolveNeighborRanks(items, insertIndex);
      const newRank = rankBetween(aboveRank, belowRank);
      await leadsService.updateLeadKanban(
        lead.id,
        sameColumn ? { kanbanRank: newRank } : { stepId: toColumnId, kanbanRank: newRank },
      );
      applyCardReorder({ ...lead, kanbanRank: newRank, stepId: toColumnId }, fromColumnId, toColumnId, items[insertIndex]?.id ?? null);
    } catch (error) {
      console.error('Erro ao salvar posição do lead:', error);
      toast.error('Não foi possível salvar a posição. Tente novamente.');
      void queryClient.invalidateQueries({ queryKey: ['leads-kanban'] });
    }
  };

  // Handler para marcar lead como ganho
  const handleWinLead = async (data: WinLeadData) => {
    if (!selectedLead) return;

    try {
      await leadsService.updateLead(selectedLead.id, data);

      // Refetch leads (lista + kanban) e fechar modal
      refetchAll();
      setIsWinOpen(false);
      setSelectedLead(null);

      alert('Lead marcado como ganho com sucesso!');
    } catch (error) {
      console.error('Erro ao marcar lead como ganho:', error);
      alert('Erro ao marcar lead como ganho. Tente novamente.');
    }
  };

  // Handler para marcar lead como perdido
  const handleLoseLead = async (data: LoseLeadData) => {
    if (!selectedLead) return;

    try {
      await leadsService.updateLead(selectedLead.id, data);

      // Refetch leads (lista + kanban) e fechar modal
      refetchAll();
      setIsLoseOpen(false);
      setSelectedLead(null);

      alert('Lead marcado como perdido com sucesso!');
    } catch (error) {
      console.error('Erro ao marcar lead como perdido:', error);
      alert('Erro ao marcar lead como perdido. Tente novamente.');
    }
  };

  // Handler para exportar leads
  const handleExport = async (columnId?: string) => {
    try {
      // Mesma query da listagem (toLeadQuery inclui TODOS os filtros aplicados
      // + assigneeId dos operadores) — o CSV devolve o que a tela mostra.
      const exportQuery = toLeadQuery({
        search: searchTerm.trim() || undefined,
        filters: {
          ...(selectedFunnelId ? { funnelId: selectedFunnelId } : {}),
          // Exportação acionada de uma coluna específica (etapa)
          ...(columnId ? { stepId: columnId } : {}),
        },
      });

      // Chamar serviço de exportação
      await leadsService.exportLeads(exportQuery);
    } catch (error) {
      console.error('Erro ao exportar leads:', error);
      alert('Erro ao exportar leads. Tente novamente.');
    }
  };

  // Handler para importar leads
  const handleImport = async (stepId: string) => {
    setImportStepId(stepId);
    setIsImportDialogOpen(true);
  };

  const handleImportFile = async (file: File, assignmentConfig?: any) => {
    if (!importStepId) return;

    try {
      await leadsService.importLeads(importStepId, file, assignmentConfig);
      setIsImportDialogOpen(false);
      toast.success('Importação iniciada!');
      // Note: não limpa importStepId aqui - precisa para rastrear progresso via Socket.IO
    } catch (error) {
      console.error('Erro ao importar:', error);
      toast.error('Erro ao importar leads. Tente novamente.');
      setImportStepId(null);
    }
  };

  // FAB mobile (bottom-nav): ação primária desta tela = criar lead (R0.4/R1)
  useEffect(() => {
    const onFab = (e: Event) => {
      e.preventDefault();
      setIsCreateLeadOpen(true);
    };
    window.addEventListener('troia:mobile-fab', onFab);
    return () => window.removeEventListener('troia:mobile-fab', onFab);
  }, []);

  // Handler para mudança de funil - atualiza estado e URL
  const handleFunnelChange = useCallback((funnelId: string | null) => {
    setSelectedFunnelId(funnelId);
    // Atualiza a URL para refletir o funil selecionado
    if (funnelId) {
      navigate(`/crm/${funnelId}`, { replace: true });
    } else {
      navigate('/crm', { replace: true });
    }
  }, [setSelectedFunnelId, navigate]);

  return (
    <div className="h-full flex flex-col" style={{ backgroundColor: 'var(--theme-page-background)' }}>
      {/* Main Content — o contexto (funil) agora vive no header via ContextSelector (R1);
          a FunnelsSidebar (rail) foi removida conforme o mockup aprovado. */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Toolbar única do DS (mockup app.html): funil | busca | controles (R1) */}
        <PageToolbar
          data-testid="crm-toolbar"
          left={
            <ContextSelector
              items={funnels}
              selectedId={selectedFunnelId}
              onSelect={(id) => handleFunnelChange(id)}
              onSettings={handleSettingsClick}
              placeholder="Selecionar funil"
              data-testid="crm-funnel-selector"
              footer={
                <button
                  type="button"
                  onClick={handleAddFunnel}
                  className="w-full flex items-center gap-2 px-2.5 py-2 rounded-lg text-ds-base font-medium text-left"
                  style={{ color: '#8b5cf6' }}
                  onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f1f5f9'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                  data-testid="crm-funnel-new"
                >
                  <Icon name="plus" className="w-4 h-4" />
                  Novo funil
                </button>
              }
            />
          }
          center={
            <ToolbarSearch
              value={searchTerm}
              onChange={setSearchTerm}
              placeholder="Buscar leads…"
              shrinkClass="ds-search hidden sm:flex"
              data-testid="crm-search"
            />
          }
          right={
            <>
              {/* Direita — ordem canônica (F15): Operadores → Filtros → Sort → Toggle → sep → CTA */}
              {showOperatorFilter && (
                <OperatorAvatarFilter
                  operators={operators}
                  selectedId={selectedOperatorIds[0] || null}
                  onSelect={(userId) => handleOperatorSelectionChange(userId === null ? [] : [userId])}
                  loading={isLoading}
                  data-testid="crm-operators"
                />
              )}
              {/* Botão Filtros (.btn com ct-badge) — abre o popover de filtros avançados */}
              <LeadsAdvancedFiltersComponent
                variant="toolbar"
                filters={advancedFilters}
                onFiltersChange={handleFiltersChange}
                alertLevelFilter={alertLevelFilter}
                onAlertLevelFilterChange={setAlertLevelFilter}
                captureFormId={selectedFunnelData?.capture?.formId}
              />
              {(
                <KanbanSortSelector value={sortMode} onChange={setSortMode} modes={LEAD_KANBAN_SORT_MODES} />
              )}
              {/* Tamanho do card — só no kanban; DEPOIS do toggle de visualização
                  (spec § 3). Legenda colapsa no padrão do DS
                  (hidden 2xl:@tb:inline, via ViewToggle). */}
              {(
                <span className="inline-flex flex-none">
                  <CardDensitySelector
                    value={cardDensity}
                    onChange={setCardDensity}
                    data-testid="crm-card-density"
                  />
                </span>
              )}
              {/* Captura por QR Code — secundário, antes do separador + CTA (mockup 12 / qr-shell.js:85) */}
              {showCaptureButton && (
                <Button
                  variant="secondary"
                  icon="qr-code"
                  collapsibleLabel
                  title="Captura por QR Code"
                  onClick={() => setIsCaptureOpen(true)}
                  data-testid="crm-capture-qr-btn"
                >
                  Captura QR
                </Button>
              )}
              {crmMenuActions.length > 0 && (
                <CardActionsMenu actions={crmMenuActions} triggerTone="theme" data-testid="crm-actions-menu" />
              )}
              {/* .tb-sep antes do CTA */}
              <span className="hidden md:block w-px h-[22px] flex-none" style={{ backgroundColor: 'var(--theme-input-border)' }} />
              {/* Novo lead (único primário) */}
              <Button
                variant="primary"
                icon="plus"
                collapsibleLabel
                onClick={() => setIsCreateLeadOpen(true)}
                data-testid="crm-new-lead"
              >
                Novo lead
              </Button>
            </>
          }
          mobile={
            /* `.m-sub` do mockup: funil (15px semibold) + chip Operadores + chip Filtros */
            <>
              <ContextSelector
                items={funnels}
                selectedId={selectedFunnelId}
                onSelect={(id) => handleFunnelChange(id)}
                placeholder="Selecionar funil"
                data-testid="crm-funnel-selector-mobile"
              />
              {showOperatorFilter && (
                <OperatorAvatarFilter
                  operators={operators}
                  selectedId={selectedOperatorIds[0] || null}
                  onSelect={(userId) => handleOperatorSelectionChange(userId === null ? [] : [userId])}
                  data-testid="crm-operators-mobile"
                />
              )}
              <LeadsAdvancedFiltersComponent
                variant="toolbar"
                pill
                filters={advancedFilters}
                onFiltersChange={handleFiltersChange}
                alertLevelFilter={alertLevelFilter}
                onAlertLevelFilterChange={setAlertLevelFilter}
                captureFormId={selectedFunnelData?.capture?.formId}
              />
              {showCaptureButton && (
                <Button variant="secondary" size="sm" icon="qr-code" onClick={() => setIsCaptureOpen(true)} data-testid="crm-capture-qr-btn-mobile">
                  Captura QR
                </Button>
              )}
              {/* Tamanho do card no mobile: a preferência é por dispositivo
                  (localStorage), então o celular guarda a sua. A legenda fica
                  oculta por padrão (base `hidden` — só aparece em ≥2xl). */}
              {(
                <span className="inline-flex flex-none">
                  <CardDensitySelector
                    value={cardDensity}
                    onChange={setCardDensity}
                    data-testid="crm-card-density-mobile"
                  />
                </span>
              )}
              {crmMenuActions.length > 0 && (
                <CardActionsMenu actions={crmMenuActions} size="sm" triggerTone="theme" data-testid="crm-actions-menu-mobile" />
              )}
            </>
          }
        />

        {/* Filterbar (.filterbar do mockup): só fchips + "Limpar tudo"; SOME quando vazia */}
        {(Object.values(advancedFilters).some((v) => v !== undefined && v !== '' && !(Array.isArray(v) && v.length === 0)) ||
          alertLevelFilter !== undefined) && (
          <div
            className="flex-none flex items-center flex-nowrap sm:flex-wrap gap-2 px-4 py-2 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
            style={{
              backgroundColor: 'var(--theme-header-background)',
              borderBottom: '1px solid var(--theme-header-border)',
            }}
            data-testid="crm-filterbar"
          >
            <LeadsAdvancedFiltersComponent
              variant="chips"
              filters={advancedFilters}
              onFiltersChange={handleFiltersChange}
              alertLevelFilter={alertLevelFilter}
              onAlertLevelFilterChange={setAlertLevelFilter}
              captureFormId={selectedFunnelData?.capture?.formId}
            />
          </div>
        )}

        {/* Aviso: funil sem usuários associados (D-context-gate inativo — leads invisíveis) */}
        {selectedFunnelData?.hasUsers === false && (
          <FunnelNoUsersBanner
            canAssignUsers={hasModulePermission('funnels', 'update')}
            onAssignUsers={handleAssignUsersClick}
          />
        )}

        {/* Progress Banner - Importação de Leads */}
        {isImporting && (
          <div
            className="border-b px-4 py-3"
            style={{
              backgroundColor: 'rgba(139, 92, 246, 0.1)',
              borderColor: 'rgba(139, 92, 246, 0.2)'
            }}
          >
            <div className="flex items-center gap-3 max-w-screen-2xl mx-auto">
              <Icon name="refresh" className="w-4 h-4 animate-spin" style={{ color: 'var(--theme-primary)' }} />
              <div className="flex-1">
                <p className="text-sm font-medium" style={{ color: 'var(--theme-card-text)' }}>
                  {importMessage}
                </p>
                <div className="flex items-center gap-2 mt-1">
                  <div
                    className="flex-1 h-1.5 rounded-full overflow-hidden"
                    style={{ backgroundColor: 'rgba(139, 92, 246, 0.2)' }}
                  >
                    <div
                      className="h-full rounded-full transition-all duration-300"
                      style={{
                        backgroundColor: 'var(--theme-primary)',
                        width: `${importPercentage}%`
                      }}
                    />
                  </div>
                  <span className="text-xs font-mono" style={{ color: 'var(--theme-muted-text)' }}>
                    {totalItems > 0 ? `${currentItem}/${totalItems} (${importPercentage}%)` : `${importPercentage}%`}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Content Area */}
        <div className="flex-1 overflow-auto">
          {/* Error State */}
          {error && (
            <div className="flex items-center justify-center h-full">
              <div
                className="px-6 py-4 rounded-lg border"
                style={{
                  backgroundColor: 'var(--theme-card-background)',
                  borderColor: 'var(--theme-card-border)',
                  color: 'var(--theme-card-text)'
                }}
              >
                <div className="flex flex-col items-center gap-3">
                  <svg
                    className="w-12 h-12"
                    style={{ color: 'var(--theme-error-color)' }}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                    />
                  </svg>
                  <div className="text-center">
                    <h3 className="text-lg font-semibold mb-1" style={{ color: 'var(--theme-card-text)' }}>
                      Erro ao carregar leads
                    </h3>
                    <p className="text-sm" style={{ color: 'var(--theme-muted-text)' }}>
                      {error instanceof Error ? error.message : 'Ocorreu um erro inesperado'}
                    </p>
                  </div>
                  <button
                    onClick={handleRefresh}
                    className="px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors"
                    style={{ backgroundColor: 'var(--theme-primary)' }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.opacity = '0.9';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.opacity = '1';
                    }}
                  >
                    Tentar Novamente
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Empty State: Funnel sem etapas */}
          {!error && selectedFunnelId && (!funnelSteps || funnelSteps.length === 0) && (
            <div className="flex items-center justify-center h-full">
              <div
                className="px-6 py-8 rounded-lg border max-w-md"
                style={{
                  backgroundColor: 'var(--theme-card-background)',
                  borderColor: 'var(--theme-card-border)',
                  color: 'var(--theme-card-text)'
                }}
              >
                <div className="flex flex-col items-center gap-4 text-center">
                  <svg
                    className="w-16 h-16"
                    style={{ color: 'var(--theme-muted-text)' }}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                    />
                  </svg>
                  <div>
                    <h3 className="text-lg font-semibold mb-2" style={{ color: 'var(--theme-card-text)' }}>
                      Este funil não possui etapas
                    </h3>
                    <p className="text-sm mb-4" style={{ color: 'var(--theme-muted-text)' }}>
                      Para visualizar o kanban de leads, é necessário primeiro criar as etapas do funil.
                    </p>
                  </div>
                  <button
                    onClick={handleSettingsClick}
                    className="px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors"
                    style={{ backgroundColor: 'var(--theme-primary)' }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.opacity = '0.9';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.opacity = '1';
                    }}
                  >
                    Gerenciar Etapas do Funil
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Kanban View */}
          {!error && (!selectedFunnelId || (funnelSteps && funnelSteps.length > 0)) && (
            <KanbanBoard
              columns={kanbanColumns}
              density={cardDensity}
              renderCard={(lead: LeadKanbanCard) => (
                <LeadCard
                  lead={lead}
                  onAction={handleLeadAction}
                  showAssignee={true}
                  {...(boardData?.stepThresholds?.[lead.stepId]
                    ? { stepThreshold: boardData.stepThresholds[lead.stepId] }
                    : {})}
                />
              )}
              onCardClick={handleCardClick}
              enableDragDrop={true}
              onCardMove={handleCardMove}
              enableReorder={sortMode === 'manual'}
              onCardDrop={handleCardDrop}
              onIntraColumnDropBlocked={handleIntraColumnDropBlocked}
              onLoadMore={loadMore}
              loading={isLoading}
              emptyMessage="Nenhum lead encontrado"
              onExport={handleExport}
              onImport={handleImport}
              onShowInfo={(stepId) => {
                console.log('Step ID for integration:', stepId);
              }}
              showTotalValue={true}
              sumLabel="negócios"
              onAddCard={() => setIsCreateLeadOpen(true)}
              addCardLabel="Adicionar lead"
            />
          )}

        </div>
      </div>

      {/* Modals */}
      <LeadCaptureModal
        isOpen={isCaptureOpen}
        onClose={handleCaptureClose}
        {...(captureSessionParam ? { resumeSessionUuid: captureSessionParam } : {})}
        onCompleted={() => { refetchAll(); }}
        onConfigureFunnel={() => { setIsCaptureOpen(false); handleSettingsClick(); }}
      />

      {isSettingsOpen && (
        <FunnelsSettingsDrawer
          isOpen={isSettingsOpen}
          onClose={handleSettingsClose}
          initialView={settingsInitialView}
          initialFunnelId={settingsInitialFunnelId}
          initialTab={settingsInitialFunnelId ? 'users' : undefined}
        />
      )}

      <LeadFormDrawer
        isOpen={isCreateLeadOpen}
        onClose={() => setIsCreateLeadOpen(false)}
        mode="create"
        onSuccess={() => { refetchAll(); setIsCreateLeadOpen(false); }}
      />

      {/* Lead Action Dialogs */}
      <LeadDetailsDialog
        isOpen={isDetailsOpen}
        onClose={() => {
          setIsDetailsOpen(false);
          setSelectedLead(null);
        }}
        lead={selectedLead}
        onSuccess={handleDialogSuccess}
      />

      <LeadFormDrawer
        isOpen={isEditOpen}
        onClose={() => {
          setIsEditOpen(false);
          setSelectedLead(null);
        }}
        mode="edit"
        lead={selectedLead}
        onSuccess={() => { refetchAll(); setIsEditOpen(false); setSelectedLead(null); }}
      />

      <DeleteLeadDialog
        isOpen={isDeleteOpen}
        onClose={() => {
          setIsDeleteOpen(false);
          setSelectedLead(null);
        }}
        lead={selectedLead}
        onSuccess={handleDeleteSuccess}
      />

      {isTransferOpen && selectedLead && (
        <TransferLeadDialog
          lead={selectedLead}
          onClose={() => {
            setIsTransferOpen(false);
            setSelectedLead(null);
          }}
          onTransfer={handleTransferLead}
        />
      )}

      <WinLeadDialog
        isOpen={isWinOpen}
        onClose={() => {
          setIsWinOpen(false);
          setSelectedLead(null);
        }}
        lead={selectedLead}
        onConfirm={handleWinLead}
      />

      <LoseLeadDialog
        isOpen={isLoseOpen}
        onClose={() => {
          setIsLoseOpen(false);
          setSelectedLead(null);
        }}
        lead={selectedLead}
        onConfirm={handleLoseLead}
      />

      {/* Alert Snooze Dialog */}
      <AlertSnoozeDialog
        isOpen={isSnoozeOpen}
        lead={snoozeLead}
        onClose={() => {
          setIsSnoozeOpen(false);
          setSnoozeLead(null);
        }}
        onConfirm={handleSnoozeConfirm}
      />

      {/* Import Leads Dialog */}
      <ImportLeadsDialog
        isOpen={isImportDialogOpen}
        onClose={() => {
          setIsImportDialogOpen(false);
          setImportStepId(null);
        }}
        onImport={handleImportFile}
        stepName={importStepId ? funnelSteps?.find(s => s.id === importStepId)?.name : undefined}
        funnelId={selectedFunnelId || undefined}
      />

      {/* Bulk actions: renderizada como .lv-bulk DENTRO da view Tabela (prop bulkBar) */}
    </div>
  );
};
